import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import { 
  Package, 
  Home, 
  ShoppingCart, 
  BarChart3, 
  Settings, 
  Users, 
  Brain, 
  RefreshCw, 
  Archive,
  CreditCard,
  Truck,
  Bot,
  TrendingUp,
  Database,
  Shield,
  ChevronRight,
  Building2
} from 'lucide-react';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarFooter,
  useSidebar
} from "@/components/ui/sidebar";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useAuth } from '@/components/auth/AuthProvider';
import { useAdminAuth } from '@/hooks/useAdminAuth';
import { useUserMode } from '@/stores/userModeStore';

interface NavigationItem {
  title: string;
  icon: React.ComponentType<any>;
  url?: string;
  items?: NavigationItem[];
  roles?: string[];
  adminOnly?: boolean;
}

const navigationItems: NavigationItem[] = [
  {
    title: "Staff Dashboard",
    icon: Home,
    url: "/staff-dashboard",
    roles: ['admin', 'manager', 'employee']
  },
  {
    title: "Admin Dashboard", 
    icon: Home,
    url: "/admin-dashboard",
    adminOnly: true
  },
  {
    title: "Workstation Hub",
    icon: Package,
    items: [
      { title: "Active Orders", icon: ShoppingCart, url: "/orders", roles: ['admin', 'manager', 'employee'] },
      { title: "Archived Orders", icon: Archive, url: "/archived-orders", roles: ['admin', 'manager'] },
      { title: "Missing Products", icon: Package, url: "/non-scanning-products", roles: ['admin', 'manager', 'employee'] },
    ],
    roles: ['admin', 'manager', 'employee']
  },
  {
    title: "AI Intelligence Center",
    icon: Brain,
    items: [
      { title: "Executive Reporting", icon: TrendingUp, url: "/executive-reporting", roles: ['admin', 'manager'] },
      { title: "AI Features", icon: Bot, url: "/ai-features", roles: ['admin', 'manager'] },
      { title: "Advanced AI", icon: Brain, url: "/ai-advanced", roles: ['admin', 'manager'] },
      { title: "Analytics Dashboard", icon: BarChart3, url: "/analytics", roles: ['admin', 'manager'] },
    ],
    roles: ['admin', 'manager']
  },
  {
    title: "Data Control Center",
    icon: RefreshCw,
    items: [
      { title: "Unified Sync Center", icon: RefreshCw, url: "/sync-manager", roles: ['admin', 'manager'] },
      { title: "Data Control", icon: Settings, url: "/sync-control", roles: ['admin', 'manager'] },
      { title: "Data Recovery", icon: Database, url: "/data-recovery", roles: ['admin'] },
    ],
    roles: ['admin', 'manager']
  },
  {
    title: "Master Data Hub",
    icon: Database,
    items: [
      { title: "Master Products", icon: Package, url: "/master-products", roles: ['admin', 'manager'] },
      { title: "Product Families", icon: Building2, url: "/product-families", roles: ['admin', 'manager'] },
      { title: "Suppliers", icon: Truck, url: "/suppliers", roles: ['admin', 'manager'] },
      { title: "Edits Awaiting Approval", icon: Shield, url: "/edits-awaiting-approval", roles: ['admin'] },
    ],
    roles: ['admin', 'manager']
  },
  {
    title: "Operations",
    icon: CreditCard,
    items: [
      { title: "Credit Requests", icon: CreditCard, url: "/credit-request", roles: ['admin', 'manager', 'employee'] },
    ],
    roles: ['admin', 'manager', 'employee']
  },
  {
    title: "HR Management",
    icon: Users,
    items: [
      { title: "User Management", icon: Users, url: "/hr/user-management", roles: ['admin', 'manager'] },
      { title: "Employee Scheduling", icon: Users, url: "/hr/scheduling", roles: ['admin', 'manager'] },
      { title: "Employee Documents", icon: Users, url: "/hr/documents", roles: ['admin', 'manager'] },
      { title: "Employee Certifications", icon: Users, url: "/hr/certifications", roles: ['admin', 'manager'] },
      { title: "Time & Attendance", icon: Users, url: "/hr/time-attendance", roles: ['admin', 'manager'] },
      { title: "Employee Alerts", icon: Users, url: "/hr/alerts", roles: ['admin', 'manager'] },
      { title: "Payroll Processing", icon: Users, url: "/payroll-processing", adminOnly: true },
    ],
    roles: ['admin', 'manager']
  }
];

const SidebarNavGroup = ({ item }: { item: NavigationItem }) => {
  const location = useLocation();
  const { state } = useSidebar();
  const { profile, isAdmin } = useAuth();
  const { isAdminAuthenticated } = useAdminAuth();
  const { currentMode } = useUserMode();
  
  const isActive = (url: string) => location.pathname === url;
  const isGroupActive = item.items?.some(subItem => subItem.url && isActive(subItem.url));
  
  const hasAccess = (navItem: NavigationItem) => {
    const isEffectiveAdmin = isAdmin && currentMode === 'admin';
    const userRole = profile?.role || 'employee';
    
    // Admin-only items require effective admin access
    if (navItem.adminOnly && !isEffectiveAdmin) return false;
    
    // If no specific roles defined, allow access
    if (!navItem.roles) return true;
    
    // Check role-based access
    if (navItem.roles.includes('admin') && !isEffectiveAdmin) return false;
    if (navItem.roles.includes('manager') && !isEffectiveAdmin && userRole !== 'manager') return false;
    
    return navItem.roles.includes(userRole);
  };

  if (!hasAccess(item)) return null;

  if (!item.items) {
    return (
      <SidebarMenuItem>
        <SidebarMenuButton asChild isActive={item.url ? isActive(item.url) : false}>
          <Link to={item.url || '#'}>
            <item.icon className="w-4 h-4" />
            <span>{item.title}</span>
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  }

  const visibleItems = item.items.filter(hasAccess);
  if (visibleItems.length === 0) return null;

  return (
    <Collapsible
      asChild
      defaultOpen={isGroupActive}
      className="group/collapsible"
    >
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton 
            tooltip={item.title}
            isActive={isGroupActive}
          >
            <item.icon className="w-4 h-4" />
            <span>{item.title}</span>
            <ChevronRight className="ml-auto h-4 w-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub>
            {visibleItems.map((subItem) => (
              <SidebarMenuSubItem key={subItem.title}>
                <SidebarMenuSubButton asChild isActive={subItem.url ? isActive(subItem.url) : false}>
                  <Link to={subItem.url || '#'}>
                    <subItem.icon className="w-4 h-4" />
                    <span>{subItem.title}</span>
                  </Link>
                </SidebarMenuSubButton>
              </SidebarMenuSubItem>
            ))}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
};

export const AppSidebar = () => {
  const { profile, isAdmin } = useAuth();
  const { isAdminAuthenticated } = useAdminAuth();
  const { currentMode } = useUserMode();

  return (
    <Sidebar variant="inset">
      <SidebarHeader>
        <div className="flex items-center gap-2 px-2 py-1">
          <Package className="h-6 w-6 text-primary" />
          <div className="flex flex-col">
            <span className="text-sm font-semibold">Foodland Eye</span>
            <span className="text-xs text-muted-foreground">
              {isAdmin && currentMode === 'admin' ? 'Admin Mode' : 
               isAdmin && currentMode === 'staff' ? 'Staff Mode (Admin)' :
               profile?.role || 'Employee'}
            </span>
          </div>
        </div>
      </SidebarHeader>
      
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Navigation</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {navigationItems.map((item) => (
                <SidebarNavGroup key={item.title} item={item} />
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <div className="p-2 text-xs text-muted-foreground">
          v2.0.0 - Phase 6 UI
        </div>
      </SidebarFooter>
    </Sidebar>
  );
};